import React, { useEffect, useState } from 'react';
import { ArrowRight, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchChannelDistributionDashboardData,
  type ChannelDistributionDashboardData,
  type ChannelMetricCount,
} from '../../lib/channelDistributionDashboard';

function loadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Failed to load channel distribution';
}

function formatDelta(delta: number): string {
  if (delta > 0) return `+${delta.toLocaleString()}`;
  return delta.toLocaleString();
}

function deltaClassName(delta: number): string {
  if (delta > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (delta < 0) return 'text-rose-600 dark:text-rose-400';
  return 'text-gray-500 dark:text-gray-400';
}

function CountWithDelta({ metric }: { metric: ChannelMetricCount }) {
  return (
    <span className="tabular-nums text-gray-900 dark:text-gray-100">
      {metric.total.toLocaleString()}{' '}
      <span className={`text-[11px] font-medium ${deltaClassName(metric.delta14Days)}`}>
        ({formatDelta(metric.delta14Days)})
      </span>
    </span>
  );
}

const ChannelDistributionDashboardPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ChannelDistributionDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void fetchChannelDistributionDashboardData()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(loadErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-emerald-500" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Channel distribution</h2>
        </div>
        <button
          type="button"
          onClick={() => navigate('/channels')}
          className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading channel distribution…</div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-rose-600 dark:text-rose-400">{error}</div>
      ) : data ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 font-medium">Channel</th>
                <th className="px-3 py-2 font-medium text-right">Total</th>
                <th className="px-3 py-2 font-medium text-right">Confirmed</th>
                <th className="px-3 py-2 font-medium text-right">Unsubscribed</th>
                <th className="px-3 py-2 font-medium text-right">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/80 dark:divide-gray-700/80">
              {data.rows.length === 0 ? (
                <tr className="bg-white dark:bg-gray-800">
                  <td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
                    No channels configured yet.
                  </td>
                </tr>
              ) : (
                data.rows.map((row) => (
                  <tr key={row.slug} className="bg-white dark:bg-gray-800">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{row.label}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <CountWithDelta metric={row.total} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <CountWithDelta metric={row.confirmed} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <CountWithDelta metric={row.unsubscribed} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <CountWithDelta metric={row.pending} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default ChannelDistributionDashboardPanel;
